import { useEventListener } from "@vueuse/core";
import { onMounted } from "vue";

import trackingEvents from "@/utils/trackingEvents.json";

import { useDebugBanner } from "./useDebugBanner.ts";
//@ts-ignore

declare global {
  interface Window {
    _paq: any;
  }
}

export type MatomoEvent = {
  category: string;
  action: string;
  name?: string | null;
  value?: string | null;
};

/**
 * This composition function is used to track events in the application.
 *
 * @param root - If true, the event handlers will be attached. !Do not set this to true when used in a component!
 */
export const useTracking = (root = false) => {
  const { enableDebug, showBanner } = useDebugBanner();

  const trackMatomoEvent = ({
    category,
    action,
    name = null,
    value = null,
  }: MatomoEvent) => {
    if (window?._paq) {
      window._paq.push(["trackEvent", category, action, name, value]);
    }
  };

  const trackSearchCount = (searchResults: number) => {
    if (window?._paq) {
      window._paq.push([
        "setCustomUrl",
        document.URL + "&search_count=" + searchResults,
      ]);
      window._paq.push(["trackPageView"]);
    }
  };
  const trackDownloadEvent = (url: string, name?: string) => {
    if (window?._paq) {
      window._paq.push(["trackLink", `${url} ${name || ""}`, "download"]);
    }
  };

  const trackEvent = (eventId: string, value?: string | number | boolean) => {
    const event = trackingEvents.find((event) => event.id === eventId);

    if (enableDebug.value) {
      if (!event) {
        // eslint-disable-next-line no-console
        console.warn(`[Tracking]: event not found. Event ID: ${eventId}`);
      }

      // eslint-disable-next-line no-console
      console.log(`[Tracking]: ${eventId}`, value);
      showBanner("Tracking", [
        {
          label: "Event ID",
          value: eventId,
        },
        {
          label: "Value",
          value: value || "N/A",
        },
      ]);
    }

    if (!event) {
      return;
    }

    return trackMatomoEvent({
      // @ts-ignore
      category: event.category,
      // @ts-ignore
      action: event.action,
      name: `${event.name || ""};${eventId};${value || ""}`,
    });
  };

  const handleEvent = (event: Event) => {
    const getElementWithTrackingId = (
      element: HTMLElement,
    ): HTMLElement | null => {
      if (element.dataset.trackingId) {
        return element;
      }

      if (element.parentElement) {
        return getElementWithTrackingId(element.parentElement);
      }

      return null;
    };

    const target = getElementWithTrackingId(event.target as HTMLElement);

    if (target && target.dataset.trackingId) {
      trackEvent(target.dataset.trackingId, target.dataset.trackingValue);
    }
  };

  onMounted(() => {
    if (root) {
      useEventListener(document, "click", handleEvent);
    }
  });

  return {
    trackEvent,
    trackDownloadEvent,
    trackSearchCount,
  };
};
